import { Company, TemplatingType, User } from "@energy-advice/easas-api";
import { TaskTemplateDefinition } from "@energy-advice/easas-central-api";
import { TaskInputGroup } from "api/model/airflow-task/TaskInputGroup";
import { AirflowWorkerQueue } from "api/model/enums";

export type AirflowDag = {
  dagId: string;
  title: string;
  description?: string | null;
  cronSchedule: string;
  useStaging?: boolean;
  timeout: number;
  company: Company;
  tasks: AirflowTaskDto[];
  airflowCode: string;
};

export type AirflowTask = {
  taskId: AirflowTaskId;
  title: string;
  description?: string;
  timeout: number;
  pythonEnvId?: string;
  workerQueue?: AirflowWorkerQueue;
  poolSlots?: number;
  jobDataMap: Map<string, string | number | boolean>;
  configData: PythonTaskConfigData;
  dag: AirflowDag;
  downstreamTasks: string[];
  upstreamTasks: string[];
  dateCreated: Date;
  isSkipped: boolean;
  allowEditLocked: boolean;
  lastUpdated?: Date;
  lastUpdatedBy?: User;
  updateDag?: boolean;
  showStepWarning?: boolean;
};

export type AirflowTaskId = {
  dagId: string;
  taskId: string;
};

export type PythonTaskConfigData = {
  templatingType: TemplatingType;
  isTemplated: boolean;
  inputGroups: Map<string, TaskInputGroup[]>;
  template: TaskTemplateInstanceInfo;
};

export type TaskTemplateInstanceInfo = {
  templateId?: string;
  templateVersion?: string;
  definition?: TaskTemplateDefinition;
  descriptionMd?: string;
};

export type AirflowDagDto = Omit<AirflowDag, "company" | "airflowCode" | "tasks"> & {
  companyRef: string;
};

export type AirflowUpdateDagResponse = {
  deployed: boolean;
  dagDto: AirflowDagDto;
};

export type AirflowTemplate = "none" | "json" | "dween" | "airflow";

export type SelectItemType = {
  label: string;
  value: string;
  severity?: "success" | "info" | "warning" | "danger" | "secondary" | "contrast" | null | undefined;
};

export const airflowTaskTypes = [
  { label: "Standard", value: "STANDARD", severity: "info" },
  { label: "Control", value: "CONTROL", severity: "danger" },
  { label: "Report", value: "REPORT", severity: "secondary" },
] as const satisfies SelectItemType[];

export type AirflowTaskType = (typeof airflowTaskTypes)[number]["value"];

export type AirflowTaskDto = {
  id: AirflowTaskId;
  title: string;
  description?: string;
  timeout: number;
  dagTitle: string;
  downstreamTasks: string[];
  upstreamTasks: string[];
  isSkipped: boolean;
  showStepWarning: boolean;
  type: AirflowTaskType;
  template?: AirflowTemplate;
  dweenTaskTemplate?: PythonCalculationTask;
  airflowTaskTemplate?: string;
  lastUpdated?: string;
  lastUpdatedBy?: string;
  sendAlerts?: boolean;
};

export type AirflowTaskCreateResponse = {
  deployed: boolean;
  taskDto: AirflowTaskDto;
};

export type SearchResultType =
  | "INPUT_FOUND"
  | "OUTPUT_FOUND"
  | "NOT_INPUT_OR_OUTPUT"
  | "PROD_TEMPLATE_OLD"
  | "PROD_TEMPLATE_GROUPS"
  | "DASHBOARD_OLD"
  | "DASHBOARD"
  | "REPORT"
  | "NOT_FOUND";

export type AirflowSearchResult = {
  resultFound: SearchResultType;
  task: AirflowTaskDto;
};

export type AirflowSearchQuery = {
  datasets: string[];
  templates: number[];
};

export type PythonCalculationTask = {
  id: number;
  title: string;
  taskGroup: string;
  taskCode: string;
};

export type AirflowTaskHistoriesPaginatedRequest = {
  dagId: string;
  taskId: string;
  skip?: number;
  take?: number;
};

export type AirflowTaskFilter = {
  search: string;
  filters: Record<string, unknown>[];
};
